import {
  VALID_NESTED_RESOURCES,
  NestedResource,
} from '../restResourceResolver/nestedResources';
import { QueryParam, QueryParams } from '../types';
import { featureFlagClient } from '@trello/feature-flag-client';
import { Analytics } from '@trello/atlassian-analytics';

interface AllowedQueryParamsForSyncing {
  [paramName: string]: Set<QueryParam>;
}

/**
 * Flattens all the 'allowedQueryParamForSyncing' objects into one big
 * 'allowlist' of query params that indicate a delta that is safe to sync
 */
export const getAllAllowedQueryParamsForSyncing = (
  nestedResources: NestedResource[],
  result: AllowedQueryParamsForSyncing = {},
): AllowedQueryParamsForSyncing => {
  nestedResources.forEach((resource) => {
    if (resource.allowedQueryParamForSyncing) {
      Object.entries(resource.allowedQueryParamForSyncing).forEach(
        ([queryParam, allowedValues]) => {
          if (!result[queryParam]) {
            result[queryParam] = new Set<QueryParam>();
          }
          allowedValues.forEach((allowedValue) => {
            result[queryParam].add(allowedValue);
          });
        },
      );
    }

    if (resource.nestedResources) {
      getAllAllowedQueryParamsForSyncing(resource.nestedResources, result);
    }
  });

  return result;
};

const allowedQueryParamsForSyncing = getAllAllowedQueryParamsForSyncing(
  VALID_NESTED_RESOURCES,
);

/**
 * Checks the queryParams provided to the request (from classic) and will
 * return true/false based on whether this query is 'safe to sync' to the
 * Apollo cache
 */
export const shouldSyncDeltaToCache = (
  queryParams: QueryParams,
  allowedQueryParams: AllowedQueryParamsForSyncing = allowedQueryParamsForSyncing,
): boolean => {
  const unsafeDeltaRejectionsEnabled = featureFlagClient.get(
    'fep.cache-syncing-reject-unsafe-deltas',
    false,
  );
  const unsafeDeltaRejectionMetricsEnabled = featureFlagClient.get(
    'fep.cache-syncing-reject-unsafe-deltas-metrics',
    false,
  );

  // If the feature flag is disabled, just always allow the delta (it's what we were doing
  // before)
  if (!unsafeDeltaRejectionsEnabled) {
    return true;
  }

  // Check the flat list of allowed query params to determine whether this payload should
  // be rejected
  for (const [paramName, paramValue] of Object.entries(queryParams)) {
    if (
      allowedQueryParams[paramName] &&
      !allowedQueryParams[paramName].has(paramValue)
    ) {
      if (unsafeDeltaRejectionMetricsEnabled) {
        Analytics.sendOperationalEvent({
          action: 'skipped',
          actionSubject: 'cacheSyncing',
          source: '@trello/graphql',
          attributes: {
            paramName,
            paramValue,
          },
        });
      }
      return false;
    }
  }

  if (unsafeDeltaRejectionMetricsEnabled) {
    Analytics.sendOperationalEvent({
      action: 'accepted',
      actionSubject: 'cacheSyncing',
      source: '@trello/graphql',
    });
  }
  return true;
};
